/**
 * V2 Log — the create-yoink flow, the app's core verb.
 *
 * Two ways in: "Log a yoink here" on a place page (location preselected
 * via /adventure/log/{locationId}) or the nav's "Log a yoink" (search
 * existing places by name). Creating a brand-new place needs the Google
 * Places key on this origin — until then the search path says so plainly.
 *
 * Writes match V1 exactly: photos compressed client-side and uploaded to
 * the 'yoinks' bucket under {userId}/, then one rawInsert into yoinks
 * (user_id, location_id, category_id, title, description, images).
 *
 * One deliberate departure from V1 (2026-07-27): picking a place no
 * longer writes a locations row. The pick holds a pending place in
 * memory and the row is created inside onSubmit — V1's pick-time upsert
 * meant every abandoned form (especially the GPS auto-select, which
 * fired on page open) left an orphan location for the admin to sweep.
 *
 * Personal categories (docs/JOURNAL-PLAN.md §11): ONE form, ONE picker.
 * The category search offers the site's curated categories AND the
 * user's own personal categories (🔒, "personal"), with create-new
 * inline right where a search comes up empty — the exact moment a
 * personal use case collides with the curated taxonomy. A yoink filed
 * under a personal category is a regular yoink row (category_id XOR
 * personal_category_id); it just skips the public-surface side-effects:
 * no follow, no leaderboard nudge, no set celebration, no last-category
 * stamp. Everything else — awards, share moment, Want-to-go cross-off —
 * behaves identically. Lists are not offered here at all (§10 dec. 4).
 */
import * as gateway from '../../core/gateway.js';
import { invalidate } from '../../core/query.js';
import { escapeHtml, stripHtmlTags } from '../../../assets/js/utils.js';
import {
  searchGooglePlaces, nearbyPlaces, resolvePlaceFromSuggestion, pendingFromNearby, savePendingLocation,
} from './places.js';
import {
  fetchAllCategories, categorySlug, rankCategoryMatches, foldParkMatches, getShowStateParks,
} from '../../core/categories.js';
import { categoryIcon } from '../../ui/icons.js';
import { isFollowingCategory, followCategory } from '../follows/follows.js';
import { crossOffWantToGo } from '../lists/lists.js';
import {
  loadPersonalAvailability, fetchPersonalCategoriesByOwner, createPersonalCategoryFor,
} from '../journal/journal.js';
import { canManage } from '../yoink/manage.js';
import { READ_ONLY_MESSAGE } from '../../core/settings.js';
import { markComplete } from '../community/tutorial.js';
import { syncAwards } from '../profile/awards.js';
import { fetchCategoryLeaderboard, announceRankChange } from '../category/leaderboard.js';
import { showToast } from '../../ui/toast.js';
import { getUrlParam } from '../../ui/share.js';
import { JUST_CREATED_KEY } from '../../ui/share-card.js';

// Split 2026-07-25: log.js had grown to 960 lines against the plan's ~400
// budget. Reads and photo upload moved to ./log-data.js, the pure validation
// and date helper to ./log-validate.js. validatePhotoPick and toDateInput are
// re-exported UNCHANGED — tests/v2-log.test.js imports both from here.
import {
  fetchLocation, searchLocations, resolvePhotos, fetchYoinkForEdit,
} from './log-data.js';
import { MAX_PHOTOS, validatePhotoPick, toDateInput } from './log-validate.js';

export { validatePhotoPick, toDateInput };

const MAX_TITLE_LENGTH = 100; // V1 create-yoink.js:40


/**
 * Mount the Log view.
 * @param {HTMLElement} el
 * @param {object} ctx - { store, router, params: { locationId? } }
 */
// Category picker rows: a dozen fits above the fold on a phone; opening
// the parks fold shows more, with a "keep typing" hint past that.
const CATEGORY_RESULTS_LIMIT = 12;
const CATEGORY_RESULTS_EXPANDED = 40;

export function mount(el, ctx) {
  const { store, router, params } = ctx;
  // /adventure/yoink/{id}/edit mounts this same form in edit mode
  const editYoinkId = params.id || null;
  let removedPaths = []; // existing photos taken off during an edit
  let destroyed = false;
  let location = null; // chosen place
  let categories = [];
  let category = null; // chosen category
  let parksExpanded = ''; // the search term whose folded park rows were opened
  // Shown under the pill when the category arrived silently (last-used
  // prefill) — cleared the moment the user picks one themselves
  let categoryHint = '';
  let saving = false;
  // Surviving re-renders: photos live outside the DOM.
  // Items are { file } (new upload) or { path } (already in storage).
  let photoItems = [];
  // Title and note are state, not just DOM: any re-render (picking a
  // category, a save error) would otherwise wipe what was typed/prefilled
  let title = '';
  let note = '';
  let socialUrl = ''; // V1's "Link to Post" (create-yoink.js:461-470)
  // "Date yoinked" — the created_at stamp, as a local yyyy-mm-dd.
  // Optional on create (blank = today); on edit it's prefilled and only
  // written back when it actually changes.
  let yoinkedDate = '';
  let originalYoinkedDate = '';
  let originalCreatedAt = null;
  let originalShelfId = null; // an edit's personal filing, as loaded
  let googleSuggestions = [];
  let nearby = [];
  let autoFollow = true; // V1 parity: logging a category follows it by default
  // Personal categories (§11): the picker's second half. myShelves are
  // the SHELF OWNER's shelves (usually mine; the yoink's owner when an
  // admin edits, the preview account's under ?as=). shelf is the chosen
  // one — mutually exclusive with `category`, exactly like the columns.
  let myShelves = [];
  let shelf = null;
  // Admin-only: ?as=<userId> files the yoink under a preview account
  // (the admin Explorers tab's "+ Yoink" — V1's create-yoink previewUserId)
  const asUserId = store.get().profile?.role === 'admin' ? getUrlParam('as') : null;
  // Whose shelves the picker offers: the yoink's owner. Set in start()
  // (me on a plain create; the row's owner on an edit; the preview
  // account under ?as=). Personal options only exist when the 20260811d
  // schema is live (undefined = not probed → hidden), the
  // listsUnavailable idiom.
  let shelfOwner = null;
  const personalReady = () => store.get().personalUnavailable === false;
  // Inline create makes a shelf owned by shelfOwner. RLS allows your
  // own — or anyone's when you're an admin (20260811f: editing Brad's
  // uncategorized yoink can file it onto a brand-new shelf of BRAD's).
  const canCreateShelf = () => personalReady()
    && (shelfOwner === store.get().user?.id || store.get().profile?.role === 'admin');

  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';

  function signedOutGate() {
    el.innerHTML = `
      <nav class="v2-back" aria-label="Back"><a href="${basePath}/" data-route="discover">← Discover</a></nav>
      <div class="v2-empty">Sign in (top right) to log a yoink.</div>`;
  }

  function setStatus(text) {
    const status = el.querySelector('[data-log-status]');
    if (status) status.textContent = text;
  }

  /**
   * V1's live requirements checklist (create-yoink.js:486-508,695-749),
   * as guidance: ✓/○ per requirement. Photo is required like V1
   * (every yoink carries at least one; enforced again in onSubmit).
   */
  function requirementsHtml() {
    const check = (ok) => (ok ? '✓' : '○');
    return `
      <span class="${location ? 'req-done' : ''}">${check(!!location)} Location</span>
      <span class="${title.trim() ? 'req-done' : ''}">${check(!!title.trim())} Title</span>
      <span class="${category || shelf ? 'req-done' : ''}">${check(!!(category || shelf))} Category</span>
      <span class="${photoItems.length ? 'req-done' : ''}">${check(!!photoItems.length)} Photo</span>`;
  }

  function refreshGuidance() {
    const reqs = el.querySelector('[data-log-requirements]');
    if (reqs) reqs.innerHTML = requirementsHtml();
    const count = el.querySelector('[data-title-count]');
    if (count) count.textContent = `${title.length} / ${MAX_TITLE_LENGTH}`;
  }

  function renderForm() {
    renderFormHtml();
    renderCategoryResults(el.querySelector('input[name="categorySearch"]')?.value || '');
  }

  function renderFormHtml() {
    el.innerHTML = `
      <nav class="v2-back" aria-label="Back"><a href="${basePath}/" data-route="discover">← Discover</a></nav>
      <form class="v2-log" data-log-form novalidate>
        <h2 class="log-heading">${editYoinkId ? 'Edit yoink' : 'Log a yoink'}</h2>
        ${asUserId ? '<p class="log-hint log-as-note">Admin: this yoink will be filed under the preview account.</p>' : ''}

        <div class="v2-field-block">
          <span class="v2-field-name">Where</span>
          ${location
            ? `<div class="log-where">
                 <strong>${escapeHtml(location.display_name)}</strong>
                 ${location.formatted_address ? `<span>${escapeHtml(location.formatted_address)}</span>` : ''}
                 <button type="button" class="log-change-place" data-log-change-place>Change</button>
               </div>${moreNearbyHtml()}`
            : `<label class="v2-field">Search for the place
                 <input type="search" name="place" placeholder="Start typing a place name…" autocomplete="off">
               </label>
               <div class="log-place-results" data-nearby aria-live="polite"></div>
               <div class="log-place-results" data-place-results aria-live="polite"></div>`}
        </div>

        <label class="v2-field">Title
          <input type="text" name="title" maxlength="${MAX_TITLE_LENGTH}" required value="${escapeHtml(title)}">
          <span class="log-char-count" data-title-count>${title.length} / ${MAX_TITLE_LENGTH}</span>
        </label>

        <div class="v2-field-block">
          <span class="v2-field-name">Category</span>
          ${category
            ? `<div class="log-category-chosen">
                 <span class="log-category-pill">${categoryIcon(category, 20)}<span>${escapeHtml(category.name)}</span></span>
                 <button type="button" class="log-change-place" data-log-change-category>Change</button>
               </div>
               ${categoryHint ? `<p class="log-hint log-category-hint">${categoryHint}</p>` : ''}`
            : shelf
            ? `<div class="log-category-chosen">
                 <span class="log-category-pill">${shelf.emoji ? escapeHtml(shelf.emoji) : '🔒'} <span>${escapeHtml(shelf.name)}</span></span>
                 <button type="button" class="log-change-place" data-log-change-category>Change</button>
               </div>
               ${categoryHint ? `<p class="log-hint log-category-hint">${categoryHint}</p>` : ''}
               <p class="log-hint">A personal category — this yoink shows on your profile and the place’s page,
                 but stays off the public map, feeds and category counts.</p>`
            : `<label class="v2-field">Search categories
                 <input type="search" name="categorySearch" placeholder="Start typing — tacos, parks…" autocomplete="off">
               </label>
               <div class="log-place-results" data-category-results aria-live="polite"></div>
               <p class="log-hint">Pick the closest fit — a place can belong to plenty of categories. Categories are curated, so we may adjust them as the map grows.${personalReady() ? ' Nothing fits? Make a personal category of your own.' : ''}</p>`}
          ${category && !editYoinkId && store.get().user && !isFollowingCategory(store, category.id) ? `
          <label class="log-autofollow">
            <input type="checkbox" name="autofollow" ${autoFollow ? 'checked' : ''}>
            Follow ${escapeHtml(category.name)}
          </label>` : ''}
        </div>

        <label class="v2-field">Date yoinked${editYoinkId ? '' : ' (leave blank for today)'}
          <input type="date" name="yoinkedDate" value="${yoinkedDate}" max="${toDateInput(new Date())}" ${editYoinkId ? 'required' : ''}>
        </label>

        <label class="v2-field">Photos (at least 1, up to ${MAX_PHOTOS})
          <input type="file" name="photos" accept="image/*" multiple>
        </label>
        <div class="log-photos" data-photo-list aria-live="polite"></div>

        <label class="v2-field">Note
          <textarea name="note" rows="4" maxlength="2000"
            placeholder="What made it worth yoinking?">${escapeHtml(note)}</textarea>
        </label>

        <label class="v2-field">Link to your post (optional)
          <input type="url" name="socialUrl" value="${escapeHtml(socialUrl)}" maxlength="500"
            placeholder="Instagram, TikTok, YouTube, your blog…" autocomplete="off">
        </label>

        <div class="log-requirements" data-log-requirements>${requirementsHtml()}</div>
        <div class="v2-form-error" data-log-error role="alert"></div>
        <div class="log-status" data-log-status aria-live="polite"></div>
        <button type="submit" class="v2-btn-leather" ${saving ? 'disabled' : ''}>
          ${saving ? 'Saving…' : editYoinkId ? 'Save changes' : 'Save yoink'}
        </button>
      </form>`;
  }

  let lastSuggestedTitle = '';

  function chooseLocation(found) {
    location = found;
    // V1 :978-994: suggest the place name when the title is empty OR
    // still the previous suggestion — switching places re-suggests, a
    // customized title is never touched
    if (found?.display_name && (!title.trim() || title === lastSuggestedTitle)) {
      title = found.display_name;
      lastSuggestedTitle = found.display_name;
    }
    renderForm();
    renderPhotoList();
  }

  function setError(message) {
    const errorEl = el.querySelector('[data-log-error]');
    if (errorEl) errorEl.textContent = message;
  }

  let placeSearchTimer = null;

  async function onPlaceSearch(input) {
    const resultsEl = el.querySelector('[data-place-results]');
    if (!resultsEl) return;
    const term = input.value.trim();
    if (term.length < 2) {
      resultsEl.innerHTML = '';
      return;
    }
    try {
      const [known, suggestions] = await Promise.all([
        searchLocations(term),
        // Journal search is free; Google Autocomplete bills per request, so
        // hold it until 3 chars — 2-char inputs return junk matches anyway.
        term.length >= 3 ? searchGooglePlaces(term).catch(() => []) : [],
      ]);
      if (destroyed) return;
      // One quiet merged list: journal matches first, then map results that
      // aren't already in the journal (no section headers — too much chrome)
      const knownNames = new Set(known.map((p) => p.display_name.toLowerCase()));
      googleSuggestions = suggestions.filter((g) => !knownNames.has(g.mainText.toLowerCase()));
      const nearbyEl = el.querySelector('[data-nearby]');
      if (nearbyEl) nearbyEl.innerHTML = '';
      const knownHtml = known.map((p) => `
            <button type="button" class="log-place-option" data-pick-place="${p.id}">
              <strong>${escapeHtml(p.display_name)}</strong>
              ${p.formatted_address ? `<span>${escapeHtml(p.formatted_address)}</span>` : ''}
            </button>`).join('');
      const newHtml = googleSuggestions.map((g, i) => `
            <button type="button" class="log-place-option" data-pick-google="${i}">
              <strong>${escapeHtml(g.mainText)}</strong>
              ${g.secondaryText ? `<span>${escapeHtml(g.secondaryText)}</span>` : ''}
            </button>`).join('');
      resultsEl.innerHTML = knownHtml + newHtml ||
        '<div class="log-hint">Nothing found — try a business or landmark name.</div>';
    } catch {
      resultsEl.innerHTML = '<div class="log-hint">Search isn’t reachable right now.</div>';
    }
  }

  async function pickGooglePlace(index) {
    const suggestion = googleSuggestions[index];
    if (!suggestion) return;
    const resultsEl = el.querySelector('[data-place-results]');
    if (resultsEl) resultsEl.innerHTML = '<div class="log-hint">Checking that place…</div>';
    try {
      // Details only — the location row is created at save time
      // (savePendingLocation in onSubmit), so an abandoned form no
      // longer strands an orphan row in the locations table.
      const result = await resolvePlaceFromSuggestion(suggestion);
      if (destroyed) return;
      if (result.rejected) {
        if (resultsEl) resultsEl.innerHTML =
          '<div class="log-hint">That’s a whole area — pick the specific place you visited.</div>';
        return;
      }
      chooseLocation(result.pending);
    } catch {
      if (destroyed) return;
      if (resultsEl) resultsEl.innerHTML = '<div class="log-hint">Couldn’t add that place — try again.</div>';
    }
  }

  function nearbyHint(text) {
    const nearbyEl = el.querySelector('[data-nearby]');
    if (nearbyEl) nearbyEl.innerHTML = text ? `<div class="log-hint">${text}</div>` : '';
  }

  /**
   * V1's stand-somewhere-and-it's-filled-in flow (create-yoink.js:
   * 839-912): staged status text, then the NEAREST place auto-selects
   * with the title prefilled; the rest render as "More nearby" so one
   * tap switches. Permission problems say so in plain words and the
   * search box covers the rest.
   */
  async function loadNearby() {
    if (!navigator.geolocation) return;
    nearbyHint('Detecting your location…');
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true, timeout: 10000, maximumAge: 60000,
        }));
      if (destroyed || location) return;
      nearbyHint('Finding nearby places…');
      const places = await nearbyPlaces(pos.coords.latitude, pos.coords.longitude);
      if (destroyed || location) return;
      if (!places.length) {
        nearbyHint('');
        return;
      }
      nearby = places.slice(0, 8);
      // Auto-select the nearest (V1: :869-895) — the title prefills via
      // chooseLocation's suggestion; "Change" or More nearby to switch
      pickNearby(0);
    } catch (err) {
      if (destroyed || location) return;
      const code = err?.code;
      nearbyHint(
        code === 1 ? 'Location access denied — search for the place instead.'
        : code === 3 ? 'Location request timed out — search for the place instead.'
        : 'Location unavailable — search for the place instead.'
      );
    }
  }

  /**
   * The other GPS hits, one tap away — but folded to a single line once
   * a place is chosen (user feedback 2026-07-09: don't fill the screen).
   */
  function moreNearbyHtml() {
    if (editYoinkId || !location || nearby.length < 2) return '';
    const others = nearby.filter((p) =>
      p.display_name !== location.display_name || p.formatted_address !== location.formatted_address);
    if (!others.length) return '';
    return `
      <details class="log-more-nearby">
        <summary class="log-hint">Not the right spot? ${others.length} more nearby</summary>
        <div class="log-place-results">
          ${others.slice(0, 7).map((p) => `
            <button type="button" class="log-place-option" data-pick-nearby="${nearby.indexOf(p)}">
              <strong>${escapeHtml(p.display_name)}</strong>
              ${p.formatted_address ? `<span>${escapeHtml(p.formatted_address)}</span>` : ''}
            </button>`).join('')}
        </div>
      </details>`;
  }

  function pickNearby(index) {
    const place = nearby[index];
    if (!place) return;
    // No write here: the GPS auto-select runs the moment the page opens,
    // and upserting then meant every abandoned visit left an orphan
    // location. The row is created at save time (savePendingLocation).
    chooseLocation(pendingFromNearby(place));
  }

  /** Taggable = pickable for a yoink (V1 excluded can_tag=false and Sets). */
  function taggable(cat) {
    return cat.can_tag !== false && !cat.is_set;
  }

  function renderCategoryResults(term) {
    const resultsEl = el.querySelector('[data-category-results]');
    if (!resultsEl) return;
    const needle = term.trim().toLowerCase();
    if (!needle) {
      // Nothing until you type. The followed-first suggestion list that
      // briefly lived here ate the whole form on a real catalog (user
      // feedback 2026-07-08); the last-used-category prefill carries the
      // repeat-logger convenience invisibly instead.
      resultsEl.innerHTML = '';
      return;
    }
    // Ranked, never hidden (2026-09-15; the blanket parks filter was
    // reverted here 2026-08-07 — see rankCategoryMatches). "Park" ranks
    // first for "park"; the thousands of state parks fold behind one
    // counted row unless the parks toggle is on or the row was opened.
    const ranked = rankCategoryMatches(categories.filter(taggable), needle, categories);
    const { rows: matches, folded, more } = foldParkMatches(ranked, {
      limit: parksExpanded === needle ? CATEGORY_RESULTS_EXPANDED : CATEGORY_RESULTS_LIMIT,
      showParks: getShowStateParks(),
      expanded: parksExpanded === needle,
    });
    const foldHtml = folded
      ? `<button type="button" class="log-place-option log-parks-fold" data-show-parks="${escapeHtml(needle)}">
            <strong>▸ ${folded.toLocaleString()} parks &amp; protected areas also match</strong>
            <span>show them</span>
          </button>`
      : '';
    const moreHtml = more
      ? `<div class="log-hint">${more.toLocaleString()} more — keep typing to narrow it down.</div>`
      : '';
    // The owner's personal categories ride the SAME results list (§11) —
    // site matches first, then personal, then create-new. The no-match
    // moment is exactly when someone's personal use case is colliding
    // with the curated taxonomy ("Historic Restaurant", user note
    // 2026-08-10) — the create button is right there.
    const shelfMatches = personalReady()
      ? myShelves.filter((c) => c.name.toLowerCase().includes(needle)).slice(0, 8)
      : [];
    const shelfHtml = shelfMatches.map((c) => `
          <button type="button" class="log-place-option" data-pick-shelf="${c.id}">
            <strong>${c.emoji ? `${escapeHtml(c.emoji)} ` : '🔒 '}${escapeHtml(c.name)}</strong>
            <span>personal category</span>
          </button>`).join('');
    const forOwner = shelfOwner === store.get().user?.id;
    const createHtml = canCreateShelf()
      ? `<button type="button" class="log-place-option log-shelf-create" data-new-shelf="${escapeHtml(term.trim())}">
            <strong>➕ New personal category “${escapeHtml(term.trim())}”</strong>
            <span>${forOwner ? 'your own shelf' : 'a shelf of THEIRS'} — off the public map and feeds</span>
          </button>`
      : '';
    const siteHtml = matches.map((c) => `
          <button type="button" class="log-place-option" data-pick-category="${c.id}">
            <strong>${escapeHtml(c.name)}</strong>
          </button>`).join('');
    resultsEl.innerHTML = siteHtml + foldHtml + moreHtml + shelfHtml + createHtml
      || '<div class="log-hint">No category by that name.</div>';
  }

  // Blob URLs are memoized per File and revoked on cleanup — creating a
  // fresh one on every re-render leaks memory for the life of the tab
  // (it adds up during a long edit session on iPad).
  const objectUrls = new Map();
  function fileUrl(file) {
    if (!objectUrls.has(file)) objectUrls.set(file, URL.createObjectURL(file));
    return objectUrls.get(file);
  }

  function photoThumbUrl(item) {
    return item.path ? gateway.getPublicUrl('yoinks', item.path) : fileUrl(item.file);
  }

  function renderPhotoList() {
    const listEl = el.querySelector('[data-photo-list]');
    if (!listEl) return;
    listEl.innerHTML = photoItems
      .map((item, i) => `
        <div class="log-photo-row">
          <img class="log-photo-thumb" src="${photoThumbUrl(item)}" alt="">
          <span class="log-photo-name">${i === 0 ? '<span class="log-cover-badge">Cover</span>' : `Photo ${i + 1}`}</span>
          ${i > 0 ? `<button type="button" class="log-change-place" data-move-photo="${i}" aria-label="Move photo ${i + 1} up">↑ Up</button>` : ''}
          <button type="button" class="log-change-place" data-remove-photo="${i}">Remove</button>
        </div>`)
      .join('') + (photoItems.length > 1
        ? '<div class="log-hint">The first photo is the cover — ↑ Up to reorder.</div>'
        : '');
  }

  /** The Set this category belongs to, if any (walk up the catalog). */
  function findSetAncestor(categoryId) {
    const byId = new Map(categories.map((c) => [c.id, c]));
    let cur = byId.get(categoryId);
    while (cur?.parent_id) {
      const parent = byId.get(cur.parent_id);
      if (!parent) return null;
      if (parent.is_set) return parent;
      cur = parent;
    }
    return null;
  }

  /**
   * V1's Set-discovery celebration (create-yoink.js:1824-1997): the
   * FIRST yoink in a collection gets a moment — 1 of N. V1 was a
   * full-screen overlay; V2 celebrates the way its milestones do,
   * with a celebrate toast.
   */
  async function checkFirstInSet(categoryId) {
    const me = store.get().user;
    const setCat = findSetAncestor(categoryId);
    if (!me || !setCat) return null;
    const subtree = new Set([setCat.id]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const c of categories) {
        if (c.parent_id && subtree.has(c.parent_id) && !subtree.has(c.id)) {
          subtree.add(c.id);
          grew = true;
        }
      }
    }
    const { data, error } = await gateway.rawQuery(gateway.TABLES.YOINKS, {
      select: 'category_id',
      filters: { user_id: `eq.${me.id}` },
      limit: 1000,
    });
    if (error) return null;
    const already = (data || []).some((y) => subtree.has(y.category_id));
    return already ? null : setCat;
  }

  /** The shelf owner's personal categories; [] when schema unavailable. */
  async function loadMyShelves() {
    myShelves = personalReady() ? await fetchPersonalCategoriesByOwner(shelfOwner) : [];
  }

  /** Make the shelf the search term names and choose it (§11 inline create). */
  async function createShelfInline(name) {
    if (!name || !canCreateShelf()) return;
    try {
      const created = await createPersonalCategoryFor(shelfOwner, name);
      if (destroyed) return;
      if (created) {
        myShelves = [...myShelves, created];
        shelf = created; // made for this yoink — chosen
        category = null;
        categoryHint = '';
      }
      renderForm();
      renderPhotoList();
      refreshGuidance();
    } catch (err) {
      setError(err?.message || 'Couldn’t create that category.');
    }
  }

  async function onSubmit(form) {
    const user = store.get().user;
    title = form.elements.title.value.trim();
    note = form.elements.note.value.trim();
    socialUrl = form.elements.socialUrl.value.trim();
    if (socialUrl && !/^https?:\/\//i.test(socialUrl)) socialUrl = `https://${socialUrl}`;
    yoinkedDate = form.elements.yoinkedDate?.value || '';
    if (!location) return setError('Pick the place first.');
    if (!title) return setError('Give it a title.');
    if (!category && !shelf) return setError('Choose a category — or make a personal one.');
    if (!photoItems.length) return setError('Add at least one photo.');
    if (editYoinkId && !yoinkedDate) return setError('Pick the date you yoinked it.');
    // yyyy-mm-dd strings compare correctly as strings
    if (yoinkedDate && yoinkedDate > toDateInput(new Date())) return setError('The date yoinked can’t be in the future.');
    // Exactly one of the two shelf pointers (the yoinks_one_category_kind
    // rule). A personal filing skips the public side-effects below.
    const categoryId = category?.id || null;
    const personal = !category && !!shelf;
    setError('');
    saving = true;
    renderForm();
    renderPhotoList();
    try {
      // A place picked this visit becomes a database row only now — the
      // deferred create that keeps abandoned forms from leaving orphan
      // locations. Fails fast, before any photo uploads.
      if (location.pending) {
        setStatus('Adding the place to the map…');
        location = await savePendingLocation(location);
        if (destroyed) return;
      }
      // Pre-save: is this about to start a collection? (must run before
      // the insert lands, or the answer is always "already started")
      // Alongside it, snapshot the category's standings so the post-save
      // rank nudge can see what this yoink changed — cached, so a hop
      // from the category page reuses the board already on screen.
      const [startingSet, standingsBefore] = (editYoinkId || personal)
        ? [null, null]
        : await Promise.all([
          checkFirstInSet(categoryId).catch(() => null),
          asUserId ? null : fetchCategoryLeaderboard(categoryId).catch(() => null),
        ]);
      const images = await resolvePhotos(user.id, photoItems, setStatus);
      // The celebration moment (2026-07-18): after a save lands and
      // caches are invalidated, record any award tiers it crossed and
      // toast them (body-level toasts outlive the navigation). Returns
      // its promise so the create path can defer the Want-to-go
      // cross-off behind the Checked It Off detection.
      const checkAwards = () => {
        const username = store.get().profile?.username;
        return syncAwards(store, user.id, {
          announce: true,
          onOpen: username ? () => router.navigate('user-awards', { username }) : null,
        }).catch(() => {});
      };
      setStatus('Saving…');
      if (editYoinkId) {
        const body = {
          location_id: location.id,
          category_id: categoryId,
          title,
          description: note || null,
          social_url: socialUrl || null,
          images,
        };
        // Recategorizing can cross the site/personal line in either
        // direction. Only mention the column when it's in play — a plain
        // site-category edit must not 400 if 20260811d hasn't landed.
        if (shelf || originalShelfId) body.personal_category_id = shelf?.id || null;
        // created_at is the "Yoinked" stamp everywhere AND the feed sort
        // key. Move only the date, keeping the original time-of-day, so
        // same-day ordering doesn't shuffle.
        if (yoinkedDate !== originalYoinkedDate && originalCreatedAt) {
          const [y, m, d] = yoinkedDate.split('-').map(Number);
          const stamp = new Date(originalCreatedAt);
          stamp.setFullYear(y, m - 1, d);
          body.created_at = stamp.toISOString();
        }
        const { error } = await gateway.rawUpdate(gateway.TABLES.YOINKS, {
          filters: { id: `eq.${editYoinkId}` },
          body,
        });
        if (error) throw error;
        // Removed photos leave storage after the row is safely updated
        for (const path of removedPaths) {
          gateway.deleteFile('yoinks', path).catch(() => {});
        }
        invalidate();
        // Backdating "Date yoinked" can complete the calendar awards
        // (Four Seasons, Hot Streak) — same celebration check as create
        checkAwards();
        router.navigate('yoink', { id: editYoinkId });
        return;
      }
      const createBody = {
        user_id: asUserId || user.id,
        location_id: location.id,
        category_id: categoryId,
        title,
        description: note || null,
        social_url: socialUrl || null,
        images,
      };
      if (personal) createBody.personal_category_id = shelf.id;
      // A backdated create carries the picked date with the current
      // time-of-day (same rule as edit), so same-day ordering stays
      // natural. Blank means now — the column's own default. logged_at
      // records the true insert moment either way (20260725 migration).
      if (yoinkedDate) {
        const [y, m, d] = yoinkedDate.split('-').map(Number);
        const stamp = new Date();
        stamp.setFullYear(y, m - 1, d);
        createBody.created_at = stamp.toISOString();
      }
      const { data, error } = await gateway.rawInsert(gateway.TABLES.YOINKS, {
        body: createBody,
      });
      if (error) throw error;
      const created = data?.[0];
      if (!created) throw new Error('No row returned');
      if (category && autoFollow && !isFollowingCategory(store, category.id)) {
        followCategory(store, category.id).catch(() => {}); // best-effort, V1 parity
      }
      markComplete('log_yoink');
      // The yoink page turns this into the post-create share moment.
      // Skipped for admin preview-account filings — not theirs to show off.
      if (!asUserId) {
        try {
          sessionStorage.setItem(JUST_CREATED_KEY, created.id);
        } catch { /* private mode */ }
      }
      if (startingSet) {
        const total = categories.filter((c) => c.parent_id === startingSet.id).length;
        showToast(`Collection started! ${startingSet.name} — 1 of ${total}`, {
          type: 'celebrate', duration: 8000,
        });
      }
      // Last-used carries for BOTH kinds (user report 2026-08-11: a
      // fresh personal category should ride into the next yoink). Same
      // V1 key either way — V1's prefill looks the id up among site
      // categories, so a personal id is a harmless no-match there.
      try {
        localStorage.setItem('yoink_last_category', personal ? shelf.id : categoryId);
      } catch { /* private mode */ }
      invalidate(); // feeds, place logs, profile feeds all just changed
      // You went — cross the place off MY "Want to go" list (custom
      // lists stay put). Best-effort and skipped when an admin files
      // the yoink under a preview account (their list, not mine to
      // touch). The awards check runs FIRST: Checked It Off must see
      // the list entry this visit is about to remove.
      if (!asUserId) {
        // The rivalry moment (2026-07-19): a fresh standings read against
        // the pre-save snapshot — "You passed Ada" / "1 more and you pass
        // Ben" as a toast, tappable through to the category's board.
        // Best-effort like the award toasts; silent without ranks, and
        // skipped entirely for a personal filing (no board to climb).
        if (category) {
          announceRankChange({
            store, router, categoryId, categoryName: category.name,
            slug: categorySlug(category), before: standingsBefore,
          }).catch(() => {});
        }
        checkAwards().then(() => {
          crossOffWantToGo(store, location.id)
            .then((removed) => {
              if (removed) showToast('Crossed off your Want to go list ✓', { type: 'success' });
            })
            .catch(() => {});
        });
      }
      // The save side-effects above are real regardless, but only steer
      // the router if this view is still the one on screen — navigating
      // away mid-save must not yank the user to the new yoink.
      if (!destroyed) router.navigate('yoink', { id: created.id });
    } catch (err) {
      if (destroyed) return;
      saving = false;
      renderForm();
      renderPhotoList();
      setError(`Couldn’t save: ${err.message}`);
    }
  }

  function onClick(event) {
    const shelfPick = event.target.closest('[data-pick-shelf]');
    if (shelfPick) {
      shelf = myShelves.find((c) => c.id === shelfPick.dataset.pickShelf) || null;
      category = null;
      categoryHint = '';
      renderForm();
      renderPhotoList();
      return;
    }
    const newShelf = event.target.closest('[data-new-shelf]');
    if (newShelf) {
      createShelfInline(newShelf.dataset.newShelf.trim());
      return;
    }
    const pick = event.target.closest('[data-pick-place]');
    if (pick) {
      fetchLocation(pick.dataset.pickPlace).then((found) => {
        if (destroyed) return;
        chooseLocation(found);
      });
      return;
    }
    const google = event.target.closest('[data-pick-google]');
    if (google) {
      pickGooglePlace(Number(google.dataset.pickGoogle));
      return;
    }
    const near = event.target.closest('[data-pick-nearby]');
    if (near) {
      pickNearby(Number(near.dataset.pickNearby));
      return;
    }
    const showParks = event.target.closest('[data-show-parks]');
    if (showParks) {
      parksExpanded = showParks.dataset.showParks;
      renderCategoryResults(showParks.dataset.showParks);
      // Focus lands on the first opened park, where the fold row was
      el.querySelector('[data-category-results] .log-place-option')?.focus();
      return;
    }
    const pickCat = event.target.closest('[data-pick-category]');
    if (pickCat) {
      category = categories.find((c) => c.id === pickCat.dataset.pickCategory) || null;
      shelf = null;
      categoryHint = ''; // their own pick — nothing to second-guess
      renderForm();
      renderPhotoList();
      return;
    }
    const movePhoto = event.target.closest('[data-move-photo]');
    if (movePhoto) {
      const i = Number(movePhoto.dataset.movePhoto);
      [photoItems[i - 1], photoItems[i]] = [photoItems[i], photoItems[i - 1]];
      renderPhotoList();
      return;
    }
    const removePhoto = event.target.closest('[data-remove-photo]');
    if (removePhoto) {
      const [gone] = photoItems.splice(Number(removePhoto.dataset.removePhoto), 1);
      if (gone?.path) removedPaths.push(gone.path);
      renderPhotoList();
      return;
    }
    if (event.target.closest('[data-log-change-place]')) {
      location = null;
      renderForm();
      renderPhotoList();
      // The GPS hits come back as tappable options next to the search box
      // (they used to live under the chosen place; folded 2026-07-09)
      const nearbyEl = el.querySelector('[data-nearby]');
      if (nearbyEl && nearby.length && !editYoinkId) {
        nearbyEl.innerHTML = nearby.slice(0, 8).map((p, i) => `
          <button type="button" class="log-place-option" data-pick-nearby="${i}">
            <strong>${escapeHtml(p.display_name)}</strong>
            ${p.formatted_address ? `<span>${escapeHtml(p.formatted_address)}</span>` : ''}
          </button>`).join('');
      }
      return;
    }
    if (event.target.closest('[data-log-change-category]')) {
      category = null;
      shelf = null;
      categoryHint = '';
      renderForm();
      renderPhotoList();
    }
  }

  function onInput(event) {
    if (event.target.name === 'title') {
      title = event.target.value;
      refreshGuidance();
    }
    if (event.target.name === 'autofollow') autoFollow = event.target.checked;
    if (event.target.name === 'note') note = event.target.value;
    if (event.target.name === 'socialUrl') socialUrl = event.target.value;
    if (event.target.name === 'yoinkedDate') yoinkedDate = event.target.value;
    if (event.target.name === 'place') {
      // 300ms debounce like V1 (create-yoink.js:1111-1133) — without it
      // every keystroke fires a DB query AND a metered Places proxy call
      clearTimeout(placeSearchTimer);
      placeSearchTimer = setTimeout(() => onPlaceSearch(event.target), 300);
    }
    if (event.target.name === 'categorySearch') renderCategoryResults(event.target.value);
    if (event.target.name === 'photos') {
      // Accumulate across picks (the iPad picker is easier in batches),
      // validating like V1 — rejects say why instead of vanishing
      const { accepted, error } = validatePhotoPick(photoItems, [...event.target.files]);
      photoItems = [...photoItems, ...accepted];
      setError(error);
      event.target.value = '';
      renderPhotoList();
      refreshGuidance();
    }
  }

  function onFormSubmit(event) {
    if (!event.target.closest('[data-log-form]')) return;
    event.preventDefault();
    if (!saving) onSubmit(event.target);
  }

  // Arrow keys walk the place/category suggestion lists: Down from the
  // search input reaches the first option, Up/Down move between options,
  // Escape returns to the input (WCAG 2.1.1 — Tab-only traversal works
  // but is a slog through long lists).
  function onKeydown(event) {
    const { key, target } = event;
    if (key === 'ArrowDown' && (target.name === 'place' || target.name === 'categorySearch')) {
      const results = el.querySelector(
        target.name === 'place' ? '[data-place-results]' : '[data-category-results]'
      );
      const first = results?.querySelector('.log-place-option');
      if (first) {
        event.preventDefault();
        first.focus();
      }
      return;
    }
    const option = target.closest?.('.log-place-option');
    if (!option) return;
    const forCategories = Boolean(option.closest('[data-category-results]'));
    const backToInput = () =>
      el.querySelector(forCategories ? 'input[name="categorySearch"]' : 'input[name="place"]')?.focus();
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      const options = [...option.parentElement.querySelectorAll('.log-place-option')];
      const next = options[options.indexOf(option) + (key === 'ArrowDown' ? 1 : -1)];
      if (next) next.focus();
      else if (key === 'ArrowUp') backToInput();
    } else if (key === 'Escape') {
      event.preventDefault();
      backToInput();
    }
  }

  document.title = 'Log a yoink | Yoink Adventures v2';

  async function start() {
    if (!store.get().user) {
      // Auth may still be settling on a cold deep link — wait for it
      if (!store.get().authReady) {
        const unsub = store.subscribe((s) => {
          if (!s.authReady || destroyed) return;
          unsub();
          start();
        });
        el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
        return;
      }
      signedOutGate();
      return;
    }
    if (store.get().isReadOnly) {
      el.innerHTML = `
        <nav class="v2-back" aria-label="Back"><a href="${basePath}/" data-route="discover">← Discover</a></nav>
        <div class="v2-empty">${READ_ONLY_MESSAGE}</div>`;
      return;
    }
    el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
    try {
      let editRow = null;
      // The 20260811d probe rides along: by first paint the store knows
      // whether to offer personal categories (undefined would hide them)
      [categories, location, editRow] = await Promise.all([
        fetchAllCategories(),
        params.locationId ? fetchLocation(params.locationId) : Promise.resolve(null),
        editYoinkId ? fetchYoinkForEdit(editYoinkId) : Promise.resolve(null),
        loadPersonalAvailability(store),
      ]);
      if (destroyed) return;
      if (editYoinkId) {
        if (!editRow) {
          el.innerHTML = '<div class="v2-empty">This yoink isn’t in the journal.</div>';
          return;
        }
        if (!canManage(store, editRow)) {
          el.innerHTML = '<div class="v2-empty">Only the explorer who logged this (or an admin) can edit it.</div>';
          return;
        }
        document.title = 'Edit yoink | Yoink Adventures v2';
        location = editRow.locations
          ? { id: editRow.locations.id, display_name: editRow.locations.display_name, formatted_address: editRow.locations.formatted_address }
          : null;
        category = categories.find((c) => c.id === editRow.category_id) || null;
        title = editRow.title || '';
        socialUrl = editRow.social_url || '';
        originalCreatedAt = editRow.created_at || null;
        yoinkedDate = toDateInput(originalCreatedAt);
        originalYoinkedDate = yoinkedDate;
        // The V2 note field is plain text; rich V1 formatting flattens on edit
        note = editRow.description ? stripHtmlTags(editRow.description) : '';
        photoItems = (editRow.images || []).filter(Boolean).map((path) => ({ path }));
        // The picker offers the ROW OWNER's shelves — an admin editing
        // someone's personal yoink moves it between THEIR categories
        shelfOwner = editRow.user_id || store.get().user.id;
        await loadMyShelves();
        if (destroyed) return;
        originalShelfId = editRow.personal_category_id || null;
        shelf = myShelves.find((c) => c.id === originalShelfId) || null;
        renderForm();
        renderPhotoList();
        return;
      }
      shelfOwner = asUserId || store.get().user.id;
      await loadMyShelves();
      if (destroyed) return;
      if (location && !title.trim()) title = location.display_name || '';
      // Arriving from a category page picks that category (V1 parity:
      // app.js:774-789 prefilled the Category View's category the same
      // way a place page prefills its location). Beats last-used.
      if (!category) {
        const slug = getUrlParam('category');
        const match = slug && categories.find((c) => categorySlug(c) === slug);
        if (match && taggable(match)) category = match;
      }
      // Last-used category prefill (V1 create-yoink.js:659-671, same
      // localStorage key; taggable non-Set only) — repeat loggers keep
      // their groove. "Change" is one tap away.
      if (!category) {
        try {
          const lastId = localStorage.getItem('yoink_last_category');
          const last = lastId && categories.find((c) => c.id === lastId);
          // A personal id resolves against MY shelves — never the preview
          // account's under ?as= (their list can't contain my stored id)
          const lastShelf = lastId && !last && myShelves.find((s) => s.id === lastId);
          if (last && taggable(last)) {
            category = last;
            categoryHint = 'Carried over from your last yoink — change it if this one’s different.';
          } else if (lastShelf) {
            shelf = lastShelf;
            categoryHint = 'Carried over from your last yoink — change it if this one’s different.';
          }
        } catch { /* private mode */ }
      }
      // The per-shelf doors: ?personal=<id> (the shelf page's + Yoink)
      // or ?personal=<slug> (the nav carry from your own shelf page) —
      // both resolve against MY shelves and beat every category prefill
      const personalParam = getUrlParam('personal');
      if (personalParam) {
        const preShelf = myShelves.find((c) => c.id === personalParam || c.slug === personalParam);
        if (preShelf) {
          shelf = preShelf;
          category = null;
          categoryHint = '';
        }
      }
      renderForm();
      if (!location) loadNearby();
    } catch {
      if (destroyed) return;
      el.innerHTML = '<div class="v2-error">Couldn’t reach the journal.</div>';
    }
  }

  start();

  el.addEventListener('click', onClick);
  el.addEventListener('input', onInput);
  el.addEventListener('submit', onFormSubmit);
  el.addEventListener('keydown', onKeydown);

  return () => {
    destroyed = true;
    clearTimeout(placeSearchTimer);
    for (const url of objectUrls.values()) URL.revokeObjectURL(url);
    objectUrls.clear();
    el.removeEventListener('click', onClick);
    el.removeEventListener('input', onInput);
    el.removeEventListener('submit', onFormSubmit);
    el.removeEventListener('keydown', onKeydown);
  };
}
